import type { Transition } from '../../api/types';
import type { BoardColumn } from './lane-columns';

/**
 * 🔴🔴 A SUGGESTION IS NOT A PERMISSION. THAT SENTENCE IS THE WHOLE OF PH-19.
 *
 * This file used to do `transitions.allowed.filter(...)` and REFUSE the drop when the filter came
 * back empty. The API never promised that list was a permission list, and since 2026-08-17 it says
 * so in three ways at once — the field is `suggested`, the old name is documented as a deprecated
 * alias, and the response carries `transitions_are_advisory: true`. Meanwhile `performTransition`
 * *"never consults it"*: **ANY ACTIVE STATUS IS A LEGAL TARGET.**
 *
 * ⭐⭐ THE SUGGESTION IS STILL SHOWN, AND THAT IS NOT A CONSOLATION PRIZE. Offering the likely next
 * status first is the useful half of the endpoint; what was wrong was using it to say NO. So:
 *   · a suggested target is offered FIRST and marked as suggested
 *   · a non-suggested ACTIVE status is a perfectly good drop and SUCCEEDS
 *   · the API is left to refuse the things it actually refuses (below)
 *
 * ⛔⛔ FREE MOVEMENT IS NOT THE ABSENCE OF RULES. Three refusals remain, and all three are real:
 *   1. THE SAME COLUMN. A drop where the card already is is not a move, and the API answers 409
 *      `ticket is already in status 'x'` — it will not write a history row saying nothing happened.
 *   2. AN INACTIVE STATUS. `assertTargetIsUsable` answers 409 `'x' is a retired status and cannot be
 *      moved into`. A retired status stays on the board while a ticket sits in it (`lane-columns.ts`),
 *      so a lane column CAN contain one — and it must not be a drop target.
 *   3. NOT KNOWING. If we could not reach the transitions endpoint we do not know whether this
 *      server is advisory at all, so we do not offer the drop. ⭐ That is not the old gate coming
 *      back: under the old gate an ANSWER of "not suggested" refused; now only the ABSENCE of an
 *      answer does.
 *
 * ⭐ AND IT DEGRADES. `advisory` is READ from the response rather than hardcoded. Point this board
 * at a deployment older than PH-19 and it goes back to gating on the list, because that server
 * really will refuse the jump — the honest behaviour, chosen by detection rather than by assumption.
 */

export type TransitionState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      /** ⚠️ SUGGESTED, not permitted. Named so it cannot be read as the old `allowed`. */
      suggested: Transition[];
      /** `transitions_are_advisory` off the wire. `false` ⇒ gate on `suggested`, as before PH-19. */
      advisory: boolean;
    };

/**
 * One status a card may be dropped into, and whether the API SUGGESTED it.
 *
 * ⭐⭐ THE FLAG IS THE WHOLE COMPROMISE PH-19 ASKS FOR. Both kinds of target are equally legal and
 * equally will succeed; the suggested ones are offered FIRST and marked, because "what normally
 * comes next" is genuinely useful and losing it would throw away the endpoint. ⛔ The flag must
 * never be used to disable anything — grep for it and check.
 */
export interface DropTarget extends Transition {
  suggested: boolean;
}

export type DropDecision =
  /** Exactly one possible target in this column — the drop performs it. */
  | { kind: 'ok'; toStatus: string; transition: DropTarget }
  /**
   * A COLLAPSED LANE with more than one possible target inside it. ⭐ The lane is not a status, so
   * there is no single move to perform; inventing one (the first, the lowest sort_order) would be
   * the UI choosing a workflow step on the user's behalf. The drop opens a chooser instead.
   */
  | { kind: 'choose'; targets: DropTarget[] }
  | { kind: 'refused'; reason: string; because: RefusalCause };

export type RefusalCause = 'unknown' | 'same-column' | 'no-legal-target';

export interface DropInput {
  column: BoardColumn;
  ticketStatus: string;
  transitions: TransitionState;
  /**
   * 🔴 THE ACTIVE STATUS CODES, from the vocabulary — the one thing that still narrows a drop.
   * ⛔ Not optional and not defaulted to "everything". `buildColumns` deliberately keeps an INACTIVE
   * status on the board while a ticket is still sitting in it, so without this a lane column would
   * offer a retired status as a target and the API would answer 409 `is a retired status`.
   */
  activeStatuses: ReadonlySet<string>;
  /**
   * Statuses the API has refused during THIS drag, from a 409 body.
   * ⭐ This is what replaces the old "adopt the refusal as the new permission list": a refusal
   * withdraws THE TARGET THAT WAS REFUSED, and nothing else. Narrowing the whole board to whatever
   * the 409 happened to list is how a refusal used to reinstate the gate.
   */
  refusedStatuses?: ReadonlySet<string>;
}

/**
 * The statuses in this column a card in `ticketStatus` could actually be moved to.
 *
 * ⭐ ORDERED SUGGESTIONS FIRST, in the API's own order, then everything else in the column's order.
 * The chooser renders this list top-down, so "what normally comes next" is the first thing under the
 * thumb without being the only thing available.
 */
function targetsIn(input: DropInput, suggested: readonly Transition[]): DropTarget[] {
  const { column, ticketStatus, activeStatuses } = input;
  const refused = input.refusedStatuses;

  const usable = (code: string): boolean =>
    code !== ticketStatus && activeStatuses.has(code) && !refused?.has(code);

  const suggestedHere: DropTarget[] = suggested
    .filter((entry) => column.statuses.includes(entry.to_status) && usable(entry.to_status))
    .map((entry) => ({ ...entry, suggested: true }));
  const alreadyOffered = new Set(suggestedHere.map((entry) => entry.to_status));

  const rest = column.statuses
    .filter((code) => usable(code) && !alreadyOffered.has(code))
    // ⚠️ `label: null` on purpose — this client does NOT carry a copy of the vocabulary, and the
    // chooser resolves the label from `VocabularyProvider`. Inventing one here would title-case a
    // code into something that looks like a label and is one only by luck.
    .map((code): DropTarget => ({
      to_status: code,
      label: null,
      colour: null,
      note: null,
      suggested: false,
    }));

  return [...suggestedHere, ...rest];
}

/**
 * 🔴 WHY THIS COLUMN TAKES NOTHING — and there are three different answers, which is why this is not
 * one sentence with the column name substituted in.
 *
 * ⛔ THE ONE PHRASE THAT MUST NOT SURVIVE HERE IS "no transition is defined". It was true under the
 * old gate and it is now a lie that sends the reader to edit a matrix which no longer decides
 * anything — except against a pre-PH-19 deployment, where it is still exactly right, and where this
 * says so along with the reason the client believes it.
 */
function emptyColumnReason(input: DropInput, advisory: boolean): string {
  const { column, activeStatuses, refusedStatuses } = input;

  if (!advisory) {
    return column.kind === 'lane'
      ? `This PlanHub API still enforces the transition graph (it did not send transitions_are_advisory), and no transition is defined from here into any status in ${column.label}.`
      : `This PlanHub API still enforces the transition graph (it did not send transitions_are_advisory), and no transition is defined from here to ${column.label}.`;
  }

  const candidates = column.statuses.filter((code) => code !== input.ticketStatus);
  if (candidates.length === 0) return `${column.label} holds no other status to move into.`;

  if (candidates.every((code) => refusedStatuses?.has(code))) {
    return `The API has just refused every status in ${column.label} for this ticket — its own message is above.`;
  }
  if (candidates.every((code) => !activeStatuses.has(code))) {
    return candidates.length === 1
      ? `${column.label} is a retired status, and a ticket cannot be moved into one.`
      : `Every status in ${column.label} is retired, and a ticket cannot be moved into a retired status.`;
  }
  return `Nothing in ${column.label} is available as a target for this ticket right now.`;
}

export function decideDrop(input: DropInput): DropDecision {
  const { column, ticketStatus, transitions } = input;

  /**
   * ⚠️ UNCHANGED BY PH-19, AND DELIBERATELY. Dropping a card where it already is is not a move, and
   * the API says so too — `performTransition` answers 409 `ticket is already in status 'x'` rather
   * than writing a history row that records nothing happening. For a COLLAPSED lane holding the
   * card's own status this also refuses moves WITHIN that lane; expanding the lane offers them, which
   * is what the expand control is for. ⛔ It is not the transition gate wearing a hat: it looks at
   * where the card IS, never at what the API suggested.
   */
  if (column.statuses.includes(ticketStatus)) {
    return {
      kind: 'refused',
      because: 'same-column',
      reason: `Already in ${column.label}.`,
    };
  }

  if (transitions.kind === 'idle' || transitions.kind === 'loading') {
    return {
      kind: 'refused',
      because: 'unknown',
      reason: 'Checking what this ticket can be moved to…',
    };
  }

  if (transitions.kind === 'error') {
    return {
      kind: 'refused',
      because: 'unknown',
      // ⛔ Not "you cannot move this". We do not know that — we could not ask, and part of what we
      // could not ask is whether this server treats its transition list as advice at all.
      reason: `The move options could not be loaded (${transitions.message}), so no drop is offered.`,
    };
  }

  /**
   * 🔴 THE ONE LINE PH-19 IS ABOUT.
   * ⛔ ADVISORY: the suggestions ORDER the targets and never filter them.
   * ⛔ NOT ADVISORY (an older API): the suggestions ARE the permission list, as they used to be —
   *    because that server really will answer 409.
   */
  const possible = transitions.advisory
    ? targetsIn(input, transitions.suggested)
    : targetsIn(
        { ...input, activeStatuses: new Set(transitions.suggested.map((e) => e.to_status)) },
        transitions.suggested,
      );

  if (possible.length === 0) {
    return {
      kind: 'refused',
      because: 'no-legal-target',
      reason: emptyColumnReason(input, transitions.advisory),
    };
  }

  const only = possible[0];
  if (possible.length === 1 && only) {
    return { kind: 'ok', toStatus: only.to_status, transition: only };
  }

  return { kind: 'choose', targets: possible };
}

/** Which columns will accept the card — computed once per drag, so every header can show it. */
export function droppableColumnKeys(
  input: Omit<DropInput, 'column'> & { columns: readonly BoardColumn[] },
): Set<string> {
  const keys = new Set<string>();
  for (const column of input.columns) {
    if (decideDrop({ ...input, column }).kind !== 'refused') keys.add(column.key);
  }
  return keys;
}

/**
 * ⭐⭐ WHAT A 409 TEACHES, AND WHAT IT NO LONGER TEACHES.
 *
 * It DOES teach that the target it names is not available right now — a retired status, or a
 * `to_status` this account may not use — so that target is withdrawn for the rest of the drag and
 * the same drop is not offered a second time.
 *
 * ⛔ IT NO LONGER REPLACES THE WHOLE TARGET SET. The old `adoptRefusedTargets` took the refusal's
 * `allowed` list and made it the board's permission list, which under advisory semantics would
 * re-impose the very gate PH-19 removed — one 409 about one status would silently forbid every
 * status the refusal happened not to mention.
 *
 * ⭐ The refusal's suggestions ARE adopted, because they are fresher than ours, but only as
 * SUGGESTIONS: they reorder what is offered and forbid nothing.
 */
export function adoptRefusal(
  current: TransitionState,
  refusal: { suggested: Transition[]; suggestedWasSent: boolean },
): TransitionState {
  if (current.kind !== 'ready') return current;
  if (!refusal.suggestedWasSent) return current;
  // ⚠️ `advisory` is carried over from the GET rather than re-read here. Not every 409 body carries
  // the flag (the retired-status conflict does not), and reading an absent key would silently flip
  // this board back into gating mode on a server that is advisory.
  return { kind: 'ready', suggested: refusal.suggested, advisory: current.advisory };
}
